import { useCallback } from "react";
import { useAuth } from "../../../auth";
import type { SupportedSession } from "../../../types";
import { useTellescopeMutation } from "../../use-tellescope-mutation";
import type { OrgOnboardingStatus } from "./types";

export interface UseUpdateOrgOnboardingStatusArgs {
  onSuccess?: () => void;
  onError?: () => void;
  session?: SupportedSession;
}

export interface UseUpdateOrgOnboardingStatusResult {
  /**
   * Shallow-merge a patch into the current status record and persist it.
   * Use for advancing/skipping a step (e.g. `{ "healthie:templates": "complete" }`).
   */
  updateStatus: (patch: Partial<OrgOnboardingStatus>) => Promise<void>;
  /**
   * Overwrite the entire status record. Use when keys must be removed (e.g.
   * integration deselection) rather than merged.
   */
  replaceStatus: (next: OrgOnboardingStatus) => Promise<void>;
}

/**
 * Returns writers for `organization.onboardingStatus`, built on
 * `useTellescopeMutation({ model: "organizations", method: "updateOne" })`.
 *
 * Reads/merges against the *current* status from auth.
 *
 * Both writers no-op (and invoke `onError`) when there is no selected organization.
 *
 * @param onSuccess Forwarded to the mutation's `onSuccess`.
 * @param onError Forwarded to the mutation's `onError`, also invoked when no org is selected.
 * @param session Optional session override.
 * @returns `{ updateStatus, replaceStatus }`.
 */
export const useUpdateOrgOnboardingStatus = ({
  onSuccess,
  onError,
  session,
}: UseUpdateOrgOnboardingStatusArgs = {}): UseUpdateOrgOnboardingStatusResult => {
  const { selectedOrganization } = useAuth();

  const updateOrganizationMutation = useTellescopeMutation({
    model: "organizations",
    method: "updateOne",
    session,
    onSuccess,
    onError,
  });

  const write = useCallback(
    async (next: OrgOnboardingStatus) => {
      if (!selectedOrganization?.id) {
        onError?.();
        return;
      }
      await updateOrganizationMutation.mutateAsync({
        id: selectedOrganization.id,
        onboardingStatus: next,
      });
    },
    [onError, selectedOrganization?.id, updateOrganizationMutation]
  );

  const updateStatus = useCallback(
    (patch: Partial<OrgOnboardingStatus>) => {
      const merged = { ...(selectedOrganization?.onboardingStatus ?? {}), ...patch };
      const next = Object.entries(merged).reduce<Record<string, string | boolean>>((acc, [key, value]) => {
        if (value !== undefined) acc[key] = value;
        return acc;
      }, {});
      return write(next);
    },
    [selectedOrganization?.onboardingStatus, write]
  );

  const replaceStatus = useCallback((next: OrgOnboardingStatus) => write(next), [write]);

  return { updateStatus, replaceStatus };
};
